import React, { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { getApiEndpoint } from '@/utils/ERPUtils';

interface ImageEnlargementModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onNavigate: (index: number) => void;
}

export const ImageEnlargementModal: React.FC<ImageEnlargementModalProps> = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onNavigate,
}) => {
  const isMobile = useIsMobile();
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  // Initialize Embla carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: currentIndex,
    loop: false,
    skipSnaps: false,
  });

  // Sync carousel selection with parent state
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const selectedIndex = emblaApi.selectedScrollSnap();
    // Ensure the selected index is within bounds
    const validIndex = Math.max(0, Math.min(selectedIndex, images.length - 1));
    if (validIndex !== currentIndex) {
      onNavigate(validIndex);
    }
  }, [emblaApi, currentIndex, onNavigate, images.length]);

  // Subscribe to carousel select events
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Sync parent currentIndex changes to carousel
  useEffect(() => {
    if (!emblaApi) return;
    // Clamp currentIndex to valid range to prevent out-of-bounds issues
    const validIndex = Math.max(0, Math.min(currentIndex, images.length - 1));
    if (emblaApi.selectedScrollSnap() !== validIndex) {
      emblaApi.scrollTo(validIndex);
    }
  }, [emblaApi, currentIndex, images.length]);

  // Lazy loading: load current and adjacent images
  useEffect(() => {
    const indicesToLoad = new Set<number>();
    indicesToLoad.add(currentIndex);
    if (currentIndex > 0) indicesToLoad.add(currentIndex - 1);
    if (currentIndex < images.length - 1) indicesToLoad.add(currentIndex + 1);

    setLoadedImages(prev => {
      const newSet = new Set(prev);
      indicesToLoad.forEach(i => newSet.add(i));
      return newSet;
    });
  }, [currentIndex, images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  const handlePrev = () => {
    if (currentIndex > 0) onNavigate(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) onNavigate(currentIndex + 1);
  };

  const handleDownload = async () => {
    const filename = `design-${currentIndex + 1}.png`;
    const imageUrl = images[currentIndex];
    try {
      // Use backend proxy to stream raw bytes from S3 (no base64 overhead)
      const proxyUrl = getApiEndpoint('DOWNLOAD_S3_IMAGE');
      const response = await fetch(`${proxyUrl}?url=${encodeURIComponent(imageUrl)}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // Last resort: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  if (!images.length) return null;

  const showDots = images.length > 1 && images.length <= 20;
  const showNavigation = images.length > 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideCloseButton={true} className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black border-none rounded-none overflow-hidden shadow-none [&>button]:!hidden">
        {/* Close button - wrapped in div to avoid [&>button]:hidden selector */}
        <div className="absolute top-4 right-4 z-50">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Embla Carousel container */}
        <div className="h-[85vh] w-full overflow-hidden" ref={emblaRef}>
          <div className="flex h-full">
            {images.map((src, idx) => (
              <div
                key={idx}
                className="flex-[0_0_100%] min-w-0 flex items-center justify-center h-full"
              >
                {loadedImages.has(idx) ? (
                  <img
                    src={src}
                    alt={`Image ${idx + 1}`}
                    className="max-w-full max-h-full object-contain"
                    draggable={false}
                  />
                ) : (
                  <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Navigation arrows (hidden on mobile - use swipe instead) */}
        {/* Wrapped in divs to avoid [&>button]:!hidden selector on DialogContent */}
        {showNavigation && !isMobile && (
          <>
            <div className="absolute left-4 top-1/2 -translate-y-1/2 z-50">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 disabled:opacity-30"
                onClick={handlePrev}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
            </div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 z-50">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 disabled:opacity-30"
                onClick={handleNext}
                disabled={currentIndex === images.length - 1}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </div>
          </>
        )}

        {/* Dot indicators (only for galleries <= 20 images) */}
        {showDots && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => onNavigate(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  idx === currentIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* Download button - wrapped in div to avoid [&>button]:!hidden */}
        <div className="absolute bottom-6 right-4 z-50">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20"
            onClick={handleDownload}
            title="Download image"
          >
            <Download className="h-5 w-5" />
          </Button>
        </div>

        {/* Image counter */}
        {showNavigation && (
          <div className="absolute top-4 left-4 text-white/70 text-sm">
            {Math.min(currentIndex + 1, images.length)} / {images.length}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
